import React from "react";

// Minimal multi-series SVG line chart (no chart-library dependency).
// series: [{ name, color, points: [{ ts, y }] }]
export function LineChart({ series, height = 280, yFormat = (v) => v.toFixed(0), title }) {
  const all = (series || []).flatMap((s) => s.points || []);
  if (all.length < 2) {
    return <p className="text-muted">Not enough data to chart yet.</p>;
  }

  const width = 760;
  const pad = { top: 16, right: 16, bottom: 28, left: 56 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const xMin = Math.min(...all.map((p) => p.ts));
  const xMax = Math.max(...all.map((p) => p.ts));
  let yMin = Math.min(...all.map((p) => p.y));
  let yMax = Math.max(...all.map((p) => p.y));
  if (yMax === yMin) { yMax += 1; yMin -= 1; }
  const ySpan = yMax - yMin;
  yMin -= ySpan * 0.05;
  yMax += ySpan * 0.05;

  const xPos = (ts) => pad.left + ((ts - xMin) / (xMax - xMin || 1)) * innerW;
  const yPos = (y) => pad.top + (1 - (y - yMin) / (yMax - yMin)) * innerH;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => yMin + f * (yMax - yMin));
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => xMin + f * (xMax - xMin));
  const fmtDate = (ts) =>
    new Date(ts).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });

  return (
    <div data-testid="line-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", maxWidth: width, height: "auto" }}
        role="img"
        aria-label={title || "Performance chart"}
      >
        {yTicks.map((t) => (
          <g key={`y-${t}`}>
            <line x1={pad.left} x2={width - pad.right} y1={yPos(t)} y2={yPos(t)} stroke="#e9ecef" />
            <text x={pad.left - 6} y={yPos(t) + 4} textAnchor="end" fontSize="11" fill="#6c757d">
              {yFormat(t)}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text key={`x-${t}`} x={xPos(t)} y={height - 8} textAnchor="middle" fontSize="11" fill="#6c757d">
            {fmtDate(t)}
          </text>
        ))}
        {(series || []).map((s) => {
          const pts = (s.points || []).filter((p) => p.y !== null && p.y !== undefined);
          if (pts.length < 2) return null;
          const d = pts
            .map((p, i) => `${i === 0 ? "M" : "L"} ${xPos(p.ts).toFixed(1)} ${yPos(p.y).toFixed(1)}`)
            .join(" ");
          return <path key={s.name} d={d} fill="none" stroke={s.color} strokeWidth="2" />;
        })}
      </svg>
      <div className="d-flex gap-3 flex-wrap">
        {(series || []).map((s) => (
          <span key={s.name} className="small">
            <span
              style={{
                display: "inline-block", width: 12, height: 3,
                backgroundColor: s.color, marginRight: 4, verticalAlign: "middle",
              }}
            />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}
